import _ from 'lodash'
import Backbone from 'backbone'
import template from './dataset-series-editor.hbs'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import * as aggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
import Dataset from 'scoreboard/datasets/model/dataset'
const { isTotal } = Dataset
import dropdown from 'scoreboard/components/dropdown'
import formHelper from 'scoreboard/components/form-helper'
import cleanDatasetSeries from 'scoreboard/datasets/clean-dataset-series'
import FormView from 'scoreboard/components/form-view'
import renderFieldDropdown from 'scoreboard/datasets/render-field-dropdown'
import DatasetSeriesForm from 'scoreboard/datasets/dataset-series-form'
import BasePercentagesOnPanel from 'scoreboard/datasets/base-percentages-on-panel'
import i18n from 'scoreboard/components/i18n'
import includeDescendantsInputLabel from './include-descendants-input-label'

var DatasetSeriesEditor = DatasetSeriesForm.extend({

	tagName: 'form',

	className: 'dataset-series-editor',

	events: {
		'click .button-primary': 'done',
		// #53910 be sure to target only the Filters panel, and not the
		// Base Percentages on panel, because that one has its own addFilter
		// handler in BasePercentagesOnPanel
		'click .filters-panel .add-filter': 'addFilter',
		'change [name=columnFieldIdentifier]': 'onChangeColumnFieldIdentifier',
		'change [name=columnAggregationType]': 'toggleBasePercentagesOn'
	},

	/**
	 * Editor for dataset series (creating them is done using dataset-series-creator.js).
	 * The options passed in here are passed into DatasetsFilterBadges, so take
	 * a look for a description of additional options there.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the dataset
	 *
	 * @param {Object} options.datasetSeries see DatasetSeries.kt
	 *
	 * @param {Object} options.treeItemsById a hash of tree items keyed by ID
	 *
	 * @param {Array} options.filters the filters applicable to the dataset series
	 *
	 * @param {Organization} options.organization the organization for the
	 * dataset series's scorecard item
	 */
	initialize: function(options = {}) {
		this.options = options
	},

	render: function() {
		this.$el.empty().append(template())

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			guard: false
		})

		this.renderAggregationType()
		this.renderCalendarField()
		this.renderFilterBadges()
		this.renderBasePercentagesOnPanel()
		this.renderIncludeDescendants()

		formHelper.writeToDom(this.options.datasetSeries, this.el)

		// AFTER formHelper.writeToDom
		this.renderFieldDropdown()

		this.formView.start()

		return this
	},

	renderFieldDropdown: function() {
		let selectedId = this.model.idsToFieldIdentifier({
			fieldId: _.get(this.options, 'datasetSeries.columnFieldId'),
			visibleLinkedFieldId: _.get(this.options, 'datasetSeries.columnVisibleLinkedFieldId')
		})

		renderFieldDropdown({
			el: this.$('[name=columnFieldIdentifier]').empty(),
			dataset: this.model,
			selectedId,
			includeTotal: true,
			includeNativeTotal: true
		})
	},

	renderAggregationType: function(e) {
		let fieldIdentifier = this.$('[name=columnFieldIdentifier]').val()
		if (_.isNil(fieldIdentifier)) {
			fieldIdentifier = this.model.idsToFieldIdentifier({
				fieldId: this.options.datasetSeries.columnFieldId,
				visibleLinkedFieldId: this.options.datasetSeries.columnVisibleLinkedFieldId
			})
		}
		let field = this.model.getFieldByFieldIdentifier(fieldIdentifier)
		let dataType = _.get(field, 'dataType')
		var options = isTotal(fieldIdentifier) ?
			aggregationTypeOptions.totalColumn() :
			aggregationTypeOptions.nonTotalColumn(dataType)
		let aggregationType = this.$('[name=columnAggregationType]').val()
		if (_.isEmpty(aggregationType)) {
			aggregationType = this.options.datasetSeries.columnAggregationType
		}
		// make sure a default aggregation type is specified
		if (!_.find(options, opt => opt.id == aggregationType)) {
			aggregationType = _.first(options).id
		}
		if (_.get(field, 'aggregationType') === 'none') {
			options = [ aggregationTypeOptions.none ]
			aggregationType = 'none'
		}
		dropdown.idLabels(this.$('[name=columnAggregationType]').empty(),
			options, aggregationType)
	},

	renderFilterBadges: function() {
		// need to _.cloneDeep the options so that the changes to the filters
		// don't appear to be automatically saved if the user cancels the dialog
		this.filterBadges = new DatasetsFilterBadges(_.cloneDeep(this.options))
		this.$('.filters-panel .panel-content').append(this.filterBadges.el)
		this.filterBadges.render()
		this.filterBadges.on('add-filter edit-filter delete-filter',
			this.formView.change.bind(this.formView))
	},

	renderBasePercentagesOnPanel: function() {
		this.basePercentagesOnPanel = new BasePercentagesOnPanel({
			model: this.model,
			formView: this.formView,
			// use duck typing
			aggregation: this.options.datasetSeries,
			table: this.options.table,
			showPercentageOf: false,
			treeItemsById: this.options.treeItemsById,
			numeratorFiltersRetriever: () => this.filterBadges.filters
		})
		this.$('.dialog-body-content').append(this.basePercentagesOnPanel.el)
		this.basePercentagesOnPanel.render()
		let updateColumnFiltersTitle = checked => {
			let key = checked ?
				'report.wizard.filters' :
				'datasets.rowFilters.clarified'
			this.$('.filters.panel h3').text(i18n.get(key))
		}
		this.basePercentagesOnPanel.toggle.on('change', updateColumnFiltersTitle)
		updateColumnFiltersTitle(this.basePercentagesOnPanel.toggle.val())
		this.toggleBasePercentagesOn()
	},

	renderIncludeDescendants () {
		let fieldLabel = includeDescendantsInputLabel({
			dataset: this.model,
			metricOrganization: this.options.organization
		})

		this.$('.include-descendants-section')
			.toggleClass('is-not-displayed', !fieldLabel)
			.find('.label-text').html(fieldLabel)
	},

	onChangeColumnFieldIdentifier: function() {
		this.renderAggregationType()
		this.renderCalendarField()
		this.toggleBasePercentagesOn()
	},

	toggleBasePercentagesOn: function() {
		let aggregationType = this.$('[name=columnAggregationType]').val()
		let percentage = aggregationType === 'percentage'
		this.$('.base-percentages-on').toggleClass('is-not-displayed', !percentage)
	},

	addFilter: function() {
		this.filterBadges.addFilter()
	},

	submit: function() {
		let datasetSeries = formHelper.readFromDom(this.el)

		// translate columnFieldIdentifier and calendarFieldIdentifier into
		// separate fieldId and visibleLinkedFieldId values
		let ids = this.model.fieldIdentifierToIds(datasetSeries.columnFieldIdentifier)
		datasetSeries.columnFieldId = ids.fieldId
		datasetSeries.columnVisibleLinkedFieldId = ids.visibleLinkedFieldId
		ids = this.model.fieldIdentifierToIds(datasetSeries.calendarFieldIdentifier)
		datasetSeries.calendarFieldId = ids.fieldId
		datasetSeries.calendarVisibleLinkedFieldId = ids.visibleLinkedFieldId
		delete datasetSeries.columnFieldIdentifier
		delete datasetSeries.calendarFieldIdentifier

		datasetSeries.filters = this.filterBadges.filters
		let equationLabel = this.model.seriesEquationLabel({
			fieldId: datasetSeries.columnFieldId,
			visibleLinkedFieldId: datasetSeries.columnVisibleLinkedFieldId
		})

		let defaults = _.omit(this.options.datasetSeries,
			'columnFieldId', 'columnVisibleLinkedFieldId',
			'calendarFieldId', 'calendarVisibleLinkedFieldId')
		_.defaults(datasetSeries, defaults)
		datasetSeries.denominatorFilters = this.basePercentagesOnPanel.denominatorFilterBadges.filters
		cleanDatasetSeries(datasetSeries)
		this.trigger('done', { datasetSeries, equationLabel })
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.filterBadges, this.basePercentagesOnPanel], 'remove')
	}

})

export default DatasetSeriesEditor

DatasetSeriesEditor.__filename__ = 'scorecards/edit/dataset-series-editor.js'
